import React from "react";

import {
  cancelArtworkOptimizationJob,
  createArtworkOptimizationJob,
  finalizeArtworkOptimizationJob,
  getArtworkOptimizationConfig,
  getArtworkOptimizationJob,
  markArtworkOptimizationUploadComplete,
  uploadArtworkOptimizationSource,
} from "../../store/artworks/artworks.api";
import {
  ArtworkOptimizationArtworkDraft,
  ArtworkOptimizationConfig,
  ArtworkOptimizationJob,
  ArtworkOptimizationPresetId,
  ArtworkOptimizationSelection,
} from "../../types/artworkOptimization";
import { Artwork } from "../../types/artworksData";

export const ACTIVE_OPTIMIZATION_JOB_KEY = "artetra:active-artwork-optimization-job";

interface UseArtworkOptimizationOptions {
  show: boolean;
  isEditing: boolean;
  initialJobId?: string | null;
  sourceFile: File | null;
  is3dModel: boolean;
  draft: ArtworkOptimizationArtworkDraft;
  isPhysical: boolean;
  hasAttachments: boolean;
  onFailed: (message: string) => void;
  onFinalized: (artwork: Artwork) => void;
  onSuccess: (message: string) => void;
  onDraftRestored: (draft: ArtworkOptimizationArtworkDraft) => void;
  onRestartPrepared?: () => void;
}

const isRecoverableStatus = (status: ArtworkOptimizationJob["status"]) =>
  !["finalized", "cancelled", "expired", "failed"].includes(status);
const isTerminalStatus = (status: ArtworkOptimizationJob["status"]) =>
  ["finalized", "cancelled", "expired", "failed"].includes(status);
const getApiErrorMessage = (caught: any, fallback: string) =>
  caught?.response?.data?.error?.message || caught?.response?.data?.msg || fallback;
const draftsMatch = (left: ArtworkOptimizationArtworkDraft | null, right: ArtworkOptimizationArtworkDraft) =>
  Boolean(left && JSON.stringify(left) === JSON.stringify(right));

export const useArtworkOptimization = ({
  show, isEditing, initialJobId, sourceFile, is3dModel, draft, isPhysical, hasAttachments,
  onFailed, onFinalized, onSuccess, onDraftRestored, onRestartPrepared,
}: UseArtworkOptimizationOptions) => {
  const [config, setConfig] = React.useState<ArtworkOptimizationConfig | null>(null);
  const [enabled, setEnabled] = React.useState(false);
  const [preset, setPreset] = React.useState<ArtworkOptimizationPresetId>("balanced");
  const [job, setJob] = React.useState<ArtworkOptimizationJob | null>(null);
  const [uploadPercent, setUploadPercent] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [working, setWorking] = React.useState(false);
  const [sourceUploadInProgress, setSourceUploadInProgress] = React.useState(false);
  const [submittedDraft, setSubmittedDraft] = React.useState<ArtworkOptimizationArtworkDraft | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  const onDraftRestoredRef = React.useRef(onDraftRestored);
  onDraftRestoredRef.current = onDraftRestored;
  const onRestartPreparedRef = React.useRef(onRestartPrepared);
  onRestartPreparedRef.current = onRestartPrepared;

  const clearPersistedJob = React.useCallback(() => {
    localStorage.removeItem(ACTIVE_OPTIMIZATION_JOB_KEY);
  }, []);

  React.useEffect(() => {
    let active = true;
    if (!show || isEditing) {
      setConfig(null);
      setEnabled(false);
      return () => { active = false; };
    }
    getArtworkOptimizationConfig()
      .then((nextConfig) => {
        if (!active) return;
        setConfig(nextConfig);
        const balancedAvailable = nextConfig.presets.some((item) => item.id === "balanced");
        setPreset(balancedAvailable ? "balanced" : (nextConfig.presets[0]?.id || "balanced"));
      })
      .catch(() => {
        if (active) {
          setConfig(null);
          setEnabled(false);
        }
      });
    return () => { active = false; };
  }, [show, isEditing]);

  React.useEffect(() => {
    if (!show || isEditing || !config?.enabled || !config.allowedForCurrentUser) return;
    const persistedJobId = localStorage.getItem(ACTIVE_OPTIMIZATION_JOB_KEY);
    const jobId = initialJobId || persistedJobId;
    if (!jobId) return;

    let active = true;
    const restore = async () => {
      try {
        const restoredJob = await getArtworkOptimizationJob(jobId);
        if (!active) return;
        if (!isRecoverableStatus(restoredJob.status)) {
          if (persistedJobId === jobId) clearPersistedJob();
          if (restoredJob.status === "failed") {
            if (restoredJob.artworkDraft) {
              onDraftRestoredRef.current(restoredJob.artworkDraft);
              setPreset(restoredJob.preset);
            }
            if (initialJobId) {
              setJob(null);
              setSubmittedDraft(null);
              setError(null);
              onRestartPreparedRef.current?.();
            } else {
              setJob(restoredJob);
              setError(restoredJob.failureReason || "Optimization failed.");
            }
          }
          return;
        }
        if (restoredJob.artworkDraft) {
          setSubmittedDraft(restoredJob.artworkDraft);
          onDraftRestoredRef.current(restoredJob.artworkDraft);
        }
        setEnabled(true);
        setPreset(restoredJob.preset);
        setJob(restoredJob);
        localStorage.setItem(ACTIVE_OPTIMIZATION_JOB_KEY, restoredJob.jobId);

        setError(null);
      } catch (caught: any) {
        if (active) {
          setError(getApiErrorMessage(caught, "Saved optimization status is temporarily unavailable."));
        }
      }
    };
    void restore();
    return () => { active = false; };
  }, [show, isEditing, initialJobId, config?.enabled, config?.allowedForCurrentUser, clearPersistedJob]);

  React.useEffect(() => {
    if (!job || !["queued", "processing"].includes(job.status)) return;
    let active = true;
    const refresh = async () => {
      try {
        const refreshedJob = await getArtworkOptimizationJob(job.jobId);
        if (!active) return;
        setJob(refreshedJob);
        setError(null);
        if (!isRecoverableStatus(refreshedJob.status)) clearPersistedJob();
      } catch {
        if (active) setError("Optimization status is temporarily unavailable. Retrying...");
      }
    };
    void refresh();
    const intervalId = window.setInterval(refresh, 2000);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [job?.jobId, job?.status, clearPersistedJob]);

  React.useEffect(() => () => { abortRef.current?.abort(); }, []);

  React.useEffect(() => {
    if (!sourceUploadInProgress) return undefined;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [sourceUploadInProgress]);

  const fileExtension = sourceFile?.name.split(".").pop()?.toLowerCase();
  const available = Boolean(
    !isEditing && sourceFile && fileExtension === "glb" && is3dModel &&
    config?.enabled && config.allowedForCurrentUser
  );
  const blockedReason = isPhysical
    ? "Optimization is available only for digital artwork."
    : hasAttachments
      ? "Remove authenticity attachments before optimizing this digital artwork."
      : null;
  const eligible = available && !blockedReason;
  const withinWorkerLimit = Boolean(
    sourceFile && config && sourceFile.size <= config.activeWorkerMaxSourceBytes
  );
  const draftChanged = Boolean(job && !draftsMatch(submittedDraft, draft));

  React.useEffect(() => {
    if (enabled && available && !eligible && !job) setEnabled(false);
  }, [enabled, available, eligible, job]);

  const start = async () => {
    if (!sourceFile || !eligible || !withinWorkerLimit) {
      setError("Select an eligible digital GLB file before starting optimization.");
      return;
    }
    setWorking(true);
    setError(null);
    setUploadPercent(0);
    const abortController = new AbortController();
    abortRef.current = abortController;
    let createdJob: ArtworkOptimizationJob | null = null;
    let sourceUploaded = false;
    try {
      const created = await createArtworkOptimizationJob({
        fileName: sourceFile.name,
        fileSize: sourceFile.size,
        contentType: "model/gltf-binary",
        preset,
        artworkDraft: draft,
      });
      createdJob = created.job;
      setJob(created.job);
      setSubmittedDraft(draft);
      setSourceUploadInProgress(true);
      await uploadArtworkOptimizationSource(created.upload, sourceFile, {
        signal: abortController.signal,
        onProgress: setUploadPercent,
      });
      sourceUploaded = true;
      localStorage.setItem(ACTIVE_OPTIMIZATION_JOB_KEY, created.job.jobId);
      const queuedJob = await markArtworkOptimizationUploadComplete(created.job.jobId);
      setJob(queuedJob);
    } catch (caught: any) {
      let cancellationConfirmed = false;
      if (createdJob && !sourceUploaded) {
        try {
          const cancelledJob = await cancelArtworkOptimizationJob(createdJob.jobId);
          cancellationConfirmed = isTerminalStatus(cancelledJob.status);
        } catch {
          // Keep the job ID so cancellation can be retried after a transient failure.
        }
      }
      if (createdJob && (sourceUploaded || !cancellationConfirmed)) {
        localStorage.setItem(ACTIVE_OPTIMIZATION_JOB_KEY, createdJob.jobId);
        setJob(createdJob);
      } else {
        clearPersistedJob();
        setJob(null);
      }
      const message = abortController.signal.aborted
        ? "Optimization was cancelled."
        : getApiErrorMessage(caught, "Optimization could not be started.");
      setError(message);
      if (!abortController.signal.aborted) onFailed(message);
    } finally {
      setSourceUploadInProgress(false);
      if (abortRef.current === abortController) abortRef.current = null;
      setWorking(false);
    }
  };

  const cancel = async () => {
    abortRef.current?.abort();
    if (!job?.jobId) return;
    try {
      const cancelledJob = await cancelArtworkOptimizationJob(job.jobId);
      if (isTerminalStatus(cancelledJob.status)) {
        clearPersistedJob();
        setJob(null);
        setSubmittedDraft(null);
      } else {
        setJob(cancelledJob);
      }
    } catch (caught: any) {
      setError(getApiErrorMessage(caught, "Cancellation could not be confirmed. The job remains recoverable."));
    }
  };

  const finalize = async (selection: ArtworkOptimizationSelection) => {
    if (!job || job.status !== "ready") return;
    if (draftChanged) {
      setError("Artwork details changed after optimization started. Rerun optimization before publishing.");
      return;
    }
    setWorking(true);
    setError(null);
    try {
      const result = await finalizeArtworkOptimizationJob(job.jobId, selection);
      clearPersistedJob();
      setJob(result.job);
      onFinalized(result.artwork);
      onSuccess(selection === "optimized" ? "Optimized artwork has been created" : "Original artwork has been created");
    } catch (caught: any) {
      const message = getApiErrorMessage(caught, "Artwork finalization failed.");
      setError(message);
      onFailed(message);
    } finally {
      setWorking(false);
    }
  };

  const rerun = async () => {
    if (job?.jobId) {
      try {
        const cancelledJob = await cancelArtworkOptimizationJob(job.jobId);
        if (!isTerminalStatus(cancelledJob.status)) {
          setError("The existing optimization job could not be closed.");
          return;
        }
      } catch (caught: any) {
        setError(getApiErrorMessage(caught, "The existing optimization job could not be closed."));
        return;
      }
    }
    clearPersistedJob();
    setJob(null);
    setSubmittedDraft(null);
    await start();
  };

  const resetForNewFile = async () => {
    abortRef.current?.abort();
    if (job?.jobId && !isTerminalStatus(job.status)) {
      try {
        const cancelledJob = await cancelArtworkOptimizationJob(job.jobId);
        if (!isTerminalStatus(cancelledJob.status)) {
          setError("The previous optimization job could not be closed.");
          return;
        }
      } catch (caught: any) {
        setError(getApiErrorMessage(caught, "The previous optimization job could not be closed."));
        return;
      }
    }
    clearPersistedJob();
    setJob(null);
    setSubmittedDraft(null);
    setError(null);
    setUploadPercent(0);
    setEnabled(false);
  };

  const active = Boolean(job && ["awaiting_upload", "queued", "processing"].includes(job.status));
  const awaitingDecision = job?.status === "ready";
  return {
    config, enabled, setEnabled, preset, setPreset, job, uploadPercent, error, working,
    available, eligible, blockedReason, withinWorkerLimit, draftChanged, active,
    awaitingDecision, start, cancel, finalize, rerun, resetForNewFile,
  };
};
